import React, { useState } from "react";
import {
  Box, Button, TextField, MenuItem, Table, TableBody, TableCell, TableHead,
  TableRow, IconButton, Menu, makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { ALLOWED_TYPES, ALLOWED_STATUSES } from "./validators";
import { G, G_SOFT, INK, BODY, MUTED, LINE, MONO, PAGE_BG, money, fieldReset } from "./tokens";

// The products list, in ProductStudio's visual language rather than a stock
// MUIDataTable. No client-side sort/search/export: the list is filtered
// server-side by type and status.

const useStyles = makeStyles(() => ({
  // ProjectsLayout's route container is height:100vh + overflow:hidden, so this
  // page owns its scrolling or rows below the fold get clipped.
  page: { height: "100vh", overflowY: "auto", backgroundColor: PAGE_BG },
  topbar: {
    position: "sticky", top: 0, zIndex: 5, display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 16, padding: "12px 22px",
    backgroundColor: "#fff", borderBottom: `1px solid ${LINE}`,
  },
  topTitle: { fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em", color: INK },
  tools: { display: "flex", alignItems: "center", gap: 8 },
  createBtn: { backgroundColor: G, color: "#fff", fontWeight: 700, "&:hover": { backgroundColor: "#03a050" } },
  input: {
    minWidth: 158,
    ...fieldReset,
    // MUI reserves arrow space for its own rendering, not for a custom
    // renderValue, so the label would run under the icon.
    "&&& .MuiSelect-select": {
      padding: "9px 34px 9px 12px",
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      fontSize: 13.5, fontWeight: 600, color: BODY,
    },
    "&&& .MuiSelect-select:focus": { background: "transparent", borderRadius: 9 },
    "& .MuiSelect-icon": { color: MUTED, right: 9 },
  },

  body: { padding: 22 },
  card: {
    background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)", overflow: "hidden",
  },
  th: {
    fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
    color: MUTED, background: "#eff3f1", borderBottom: `1px solid ${LINE}`,
    padding: "10px 14px", whiteSpace: "nowrap",
  },
  thNum: { textAlign: "right" },
  row: { cursor: "pointer", transition: "background .12s", "&:hover": { background: "#f7faf8" } },
  td: { padding: "12px 14px", borderBottom: `1px solid ${LINE}`, fontSize: 13.5, color: BODY },
  tdName: { fontWeight: 700, color: INK, fontSize: 14 },
  tdNum: { textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: INK, whiteSpace: "nowrap" },

  // A dot plus a word, not a filled chip: a table of chips fights the row text.
  status: { display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 12.5, whiteSpace: "nowrap" },
  dot: { width: 7, height: 7, borderRadius: "50%", flex: "none" },
  tag: {
    fontSize: 10.5, color: MUTED, fontWeight: 600, background: "#eff3f1",
    borderRadius: 5, padding: "2px 7px", marginRight: 4, whiteSpace: "nowrap",
  },
  typeTag: { background: G_SOFT, color: "#0f7a3d" },
  empty: { padding: "56px 22px", textAlign: "center", color: MUTED, fontSize: 13 },
}));

const currentPricing = (product) =>
  (product.pricingHistory || []).find((v) => v.effectiveTo === null) || null;

// Fixed billing enum, shown instead of the free-text label for a consistent list.
const BILLING_LABEL = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly", oneTime: "One-time" };

const fulfillmentTags = (product) => {
  const f = product.fulfillment || {};
  const tags = [];
  if (f.subscription) tags.push("Plan");
  if (f.credits) tags.push("Credits");
  if (f.webinar) tags.push("Webinar");
  return tags;
};

const ProductsTable = ({ products, filters, onFilterChange, canWrite, onCreate, onRowClick, onToggleStatus }) => {
  const classes = useStyles();
  // Row action menu, anchored to the kebab of whichever row opened it.
  const [menu, setMenu] = useState({ anchor: null, product: null });
  const openMenu = (e, p) => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, product: p }); };
  const closeMenu = () => setMenu({ anchor: null, product: null });
  const runAction = (fn) => (e) => { e.stopPropagation(); const p = menu.product; closeMenu(); fn(p); };

  return (
    <Box className={classes.page}>
      <Box className={classes.topbar}>
        <div className={classes.topTitle}>Checkout Products</div>
        <Box className={classes.tools}>
          {/* displayEmpty + renderValue: MUI renders nothing for a "" value,
              which leaves the filter looking blank. */}
          <TextField className={classes.input} select variant="outlined" size="small"
            value={filters.type || ""}
            SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? `Type · ${v}` : "All types") }}
            onChange={(e) => onFilterChange({ ...filters, type: e.target.value })}>
            <MenuItem value="">All types</MenuItem>
            {ALLOWED_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField className={classes.input} select variant="outlined" size="small"
            value={filters.status || ""}
            SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? `Status · ${v}` : "All statuses") }}
            onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}>
            <MenuItem value="">All statuses</MenuItem>
            {ALLOWED_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          {canWrite && (
            <Button className={classes.createBtn} variant="contained" size="medium"
              startIcon={<AddIcon />} onClick={onCreate}>New product</Button>
          )}
        </Box>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.th}>Name</TableCell>
                <TableCell className={classes.th}>Type</TableCell>
                <TableCell className={classes.th}>Status</TableCell>
                <TableCell className={classes.th}>Billing</TableCell>
                <TableCell className={`${classes.th} ${classes.thNum}`}>Current price</TableCell>
                <TableCell className={classes.th}>Fulfilment</TableCell>
                <TableCell className={`${classes.th} ${classes.thNum}`}>Updated</TableCell>
                {canWrite && <TableCell className={classes.th} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 && (
                <TableRow>
                  <TableCell className={classes.empty} colSpan={canWrite ? 8 : 7}>
                    No products match these filters.
                  </TableCell>
                </TableRow>
              )}
              {products.map((p) => {
                const pricing = currentPricing(p);
                const active = p.status === "active";
                return (
                  <TableRow key={p._id} className={classes.row} onClick={() => onRowClick(p)}>
                    <TableCell className={`${classes.td} ${classes.tdName}`}>{p.name}</TableCell>
                    <TableCell className={classes.td}>
                      <span className={`${classes.tag} ${classes.typeTag}`}>{p.type}</span>
                    </TableCell>
                    <TableCell className={classes.td}>
                      <span className={classes.status} style={{ color: active ? "#0f7a3d" : MUTED }}>
                        <span className={classes.dot} style={{ background: active ? G : "#c2ccc7" }} />
                        {p.status}
                      </span>
                    </TableCell>
                    <TableCell className={classes.td}>{BILLING_LABEL[p.billing?.period] || p.billing?.period || "—"}</TableCell>
                    <TableCell className={`${classes.td} ${classes.tdNum}`}>
                      {pricing ? money(pricing.basePrice, pricing.currency) : "—"}
                    </TableCell>
                    <TableCell className={classes.td}>
                      {fulfillmentTags(p).map((t) => <span key={t} className={classes.tag}>{t}</span>)}
                      {fulfillmentTags(p).length === 0 && "—"}
                    </TableCell>
                    <TableCell className={`${classes.td} ${classes.tdNum}`} style={{ fontWeight: 500, color: MUTED }}>
                      {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </TableCell>
                    {canWrite && (
                      <TableCell className={classes.td} align="right" style={{ width: 44 }}>
                        <IconButton size="small" onClick={(e) => openMenu(e, p)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Menu anchorEl={menu.anchor} open={!!menu.anchor} onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem onClick={runAction(onRowClick)}>Edit</MenuItem>
        <MenuItem onClick={runAction(onToggleStatus)}>
          {menu.product?.status === "active" ? "Deactivate" : "Activate"}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProductsTable;
